import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Save, ShoppingCart, Sell } from '@mui/icons-material';
import { toast } from 'react-toastify';
import type { CapitalPartner, PlanCapitalAllocation, StockPlan } from '../../../../../models/StockPickTypes';
import { emptyAllocation } from '../../../../../models/StockPickTypes';
import type { PlanExecutionData, PlanPurchase, PlanSell } from '../../../../../models/PlanExecutionTypes';
import {
  emptyPurchaseDraft,
  emptySellDraft,
  getPlanPurchases,
  totalSharesBought,
} from '../../../../../models/PlanExecutionTypes';
import { getCapitalPartnersBrief } from '../../../../../services/request/CapitalManagement';
import {
  deletePlanPurchase,
  deletePlanSell,
  getPlanExecution,
  savePlanPurchase,
  savePlanSell,
} from '../../../../../services/request/planExecutionApi';
import {
  buildExecutionReport,
  buildTimelineEvents,
  capitalGainsTypeForSell,
  grossAmount,
  purchaseTotalCost,
  suggestBuyCharges,
  suggestSellCharges,
  sumCharges,
} from '../../../../../utils/planExecutionCalculations';
import { formatCurrency } from '../../../../../utils/CurrencyFormat';
import IndianMarketChargesFields from './IndianMarketChargesFields';
import PlanExecutionReportSection from './PlanExecutionReportSection';
import PlanExecutionTimeline from './PlanExecutionTimeline';
import PurchasePartnerAllocations from './PurchasePartnerAllocations';
import PlanProfitOverrideSection from './PlanProfitOverrideSection';
import MarketValuationPanel from '../../../../shared/MarketValuationPanel';
import { getPlanMarketValuation } from '../../../../../services/request/MarketValuation';
import type { PlanMarketValuation } from '../../../../../models/MarketValuationTypes';

interface Props {
  plan: StockPlan;
  onSaved?: () => void;
}

/** null = list view, 'new' = adding, purchase id = editing */
type PurchaseEditorMode = null | 'new' | string;

const PlanExecutionPanel: React.FC<Props> = ({ plan, onSaved }) => {
  const [data, setData] = useState<PlanExecutionData>({ sells: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purchaseEditor, setPurchaseEditor] = useState<PurchaseEditorMode>(null);
  const [editingPurchaseId, setEditingPurchaseId] = useState<string | undefined>();
  const [purchaseDraft, setPurchaseDraft] = useState(emptyPurchaseDraft());
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [sellDraft, setSellDraft] = useState(emptySellDraft());
  const [editingSellId, setEditingSellId] = useState<string | null>(null);
  const [partners, setPartners] = useState<CapitalPartner[]>([]);
  const [partnerRows, setPartnerRows] = useState<PlanCapitalAllocation[]>([emptyAllocation()]);
  const [marketValuation, setMarketValuation] = useState<PlanMarketValuation | null>(null);
  const [valuationLoading, setValuationLoading] = useState(false);

  const purchases = useMemo(() => getPlanPurchases(data), [data]);

  const draftTotalCost = useMemo(() => {
    const gross = grossAmount(purchaseDraft.quantity, purchaseDraft.pricePerShare);
    return gross + sumCharges(purchaseDraft.charges);
  }, [purchaseDraft]);

  const loadMarketValuation = useCallback(async () => {
    if (!plan.id) return;
    setValuationLoading(true);
    try {
      setMarketValuation(await getPlanMarketValuation(plan.id));
    } catch {
      setMarketValuation(null);
    } finally {
      setValuationLoading(false);
    }
  }, [plan.id]);

  const loadExecution = useCallback(async () => {
    if (!plan.id) return;
    setLoading(true);
    try {
      const [execution, partnerList] = await Promise.all([
        getPlanExecution(plan.id),
        getCapitalPartnersBrief(),
      ]);
      setPartners(partnerList);
      setData(execution);
      setPurchaseEditor(null);
      void loadMarketValuation();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Failed to load execution data');
      setData({ sells: [] });
    } finally {
      setLoading(false);
    }
  }, [plan.id, loadMarketValuation]);

  useEffect(() => {
    loadExecution();
    setSellDialogOpen(false);
    setEditingSellId(null);
  }, [loadExecution]);

  const sharesRemaining = useMemo(() => {
    const bought = totalSharesBought(data);
    const sold = data.sells.reduce((s, x) => s + x.quantity, 0);
    return bought - sold;
  }, [data]);

  const report = useMemo(() => buildExecutionReport(data), [data]);

  const timelineEvents = useMemo(() => {
    const notes = (plan.comments ?? []).map((c) => ({
      id: c.id,
      date: c.createdDate?.slice(0, 10) ?? '1970-01-01',
      body: c.body,
    }));
    return buildTimelineEvents(data, notes);
  }, [data, plan.comments]);

  const legacyUnsaved = purchases.some((p) => !p.id);

  const openNewPurchaseForm = () => {
    setEditingPurchaseId(undefined);
    setPurchaseDraft(emptyPurchaseDraft());
    setPartnerRows([emptyAllocation()]);
    setPurchaseEditor('new');
  };

  const openEditPurchaseForm = (p: PlanPurchase) => {
    setEditingPurchaseId(p.id);
    setPurchaseDraft({
      tradeDate: p.tradeDate,
      quantity: p.quantity,
      pricePerShare: p.pricePerShare,
      charges: { ...p.charges },
      comments: p.comments ?? '',
    });
    setPartnerRows(
      p.partnerAllocations?.length
        ? p.partnerAllocations.map((a) => ({ ...a }))
        : [emptyAllocation()]
    );
    setPurchaseEditor(p.id ?? 'new');
  };

  const handleSavePurchase = async () => {
    if (!plan.id) return;
    if (purchaseDraft.quantity <= 0 || purchaseDraft.pricePerShare <= 0) {
      toast.error('Enter valid quantity and purchase price');
      return;
    }
    const payloadAllocs = partnerRows.filter((r) => r.capitalPartnerId && (r.allocatedAmount ?? 0) > 0);
    const allocSum = payloadAllocs.reduce((s, r) => s + (r.allocatedAmount || 0), 0);
    if (payloadAllocs.length === 0) {
      toast.error('Add at least one capital partner for this purchase');
      return;
    }
    if (Math.abs(allocSum - draftTotalCost) > 0.01) {
      toast.error(
        `Partner shares must total ${formatCurrency(draftTotalCost)} (currently ${formatCurrency(allocSum)})`
      );
      return;
    }
    setSaving(true);
    try {
      const next = await savePlanPurchase(plan.id, {
        ...(editingPurchaseId ? { id: editingPurchaseId } : {}),
        ...purchaseDraft,
        comments: purchaseDraft.comments?.trim() || undefined,
        partnerAllocations: payloadAllocs,
      });
      setData(next);
      setPurchaseEditor(null);
      toast.success(editingPurchaseId ? 'Purchase updated' : 'Purchase recorded');
      onSaved?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePurchase = async (purchaseId: string) => {
    if (!plan.id || !window.confirm('Delete this purchase? Partner splits for this buy will be removed.')) return;
    setSaving(true);
    try {
      const next = await deletePlanPurchase(plan.id, purchaseId);
      setData(next);
      if (purchaseEditor === purchaseId) setPurchaseEditor(null);
      toast.success('Purchase removed');
      onSaved?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const applySuggestedBuyCharges = () => {
    const gross = grossAmount(purchaseDraft.quantity, purchaseDraft.pricePerShare);
    if (gross <= 0) {
      toast.info('Enter quantity and price first');
      return;
    }
    setPurchaseDraft({ ...purchaseDraft, charges: suggestBuyCharges(gross) });
  };

  const openSellDialog = (sell?: PlanSell) => {
    if (purchases.length === 0) {
      toast.info('Record at least one purchase first');
      return;
    }
    if (sell) {
      setEditingSellId(sell.id);
      setSellDraft({
        tradeDate: sell.tradeDate,
        quantity: sell.quantity,
        pricePerShare: sell.pricePerShare,
        charges: { ...sell.charges },
        comments: sell.comments ?? '',
      });
    } else {
      setEditingSellId(null);
      setSellDraft(emptySellDraft());
    }
    setSellDialogOpen(true);
  };

  const applySuggestedSellCharges = () => {
    const gross = grossAmount(sellDraft.quantity, sellDraft.pricePerShare);
    if (gross <= 0) {
      toast.info('Enter quantity and sell price first');
      return;
    }
    setSellDraft({ ...sellDraft, charges: suggestSellCharges(gross) });
  };

  const maxSellQty = editingSellId
    ? sharesRemaining + (data.sells.find((s) => s.id === editingSellId)?.quantity ?? 0)
    : sharesRemaining;

  const handleSaveSell = async () => {
    if (!plan.id || purchases.length === 0) return;
    if (sellDraft.quantity <= 0 || sellDraft.pricePerShare <= 0) {
      toast.error('Enter valid sell quantity and price');
      return;
    }
    if (sellDraft.quantity > maxSellQty) {
      toast.error(`Only ${maxSellQty} shares available to sell`);
      return;
    }
    setSaving(true);
    try {
      const legacy = purchases.find((p) => !p.id);
      if (legacy) {
        await savePlanPurchase(plan.id, {
          tradeDate: legacy.tradeDate,
          quantity: legacy.quantity,
          pricePerShare: legacy.pricePerShare,
          charges: legacy.charges,
          comments: legacy.comments,
          partnerAllocations: data.partnerAllocations,
        });
      }
      const next = await savePlanSell(plan.id, {
        id: editingSellId ?? undefined,
        tradeDate: sellDraft.tradeDate,
        quantity: sellDraft.quantity,
        pricePerShare: sellDraft.pricePerShare,
        charges: sellDraft.charges,
        comments: sellDraft.comments?.trim() || undefined,
      });
      setData(next);
      setSellDialogOpen(false);
      toast.success(editingSellId ? 'Sell updated' : 'Sell recorded');
      onSaved?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSell = async (sellId: string) => {
    if (!plan.id) return;
    setSaving(true);
    try {
      const next = await deletePlanSell(plan.id, sellId);
      setData(next);
      toast.success('Sell removed');
      onSaved?.();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const renderPurchaseForm = () => (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Purchase date"
          type="date"
          size="small"
          value={purchaseDraft.tradeDate}
          onChange={(e) => setPurchaseDraft({ ...purchaseDraft, tradeDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
          sx={{ maxWidth: 200 }}
        />
        <TextField
          label="Shares bought"
          type="number"
          size="small"
          required
          value={purchaseDraft.quantity || ''}
          onChange={(e) => setPurchaseDraft({ ...purchaseDraft, quantity: Number(e.target.value) || 0 })}
          sx={{ maxWidth: 160 }}
        />
        <TextField
          label="Purchase price / share (INR)"
          type="number"
          size="small"
          required
          value={purchaseDraft.pricePerShare || ''}
          onChange={(e) => setPurchaseDraft({ ...purchaseDraft, pricePerShare: Number(e.target.value) || 0 })}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Gross value"
          size="small"
          disabled
          value={formatCurrency(grossAmount(purchaseDraft.quantity, purchaseDraft.pricePerShare))}
          sx={{ minWidth: 140 }}
        />
      </Stack>
      <Button size="small" variant="text" onClick={applySuggestedBuyCharges} sx={{ alignSelf: 'flex-start' }}>
        Suggest Indian delivery charges
      </Button>
      <IndianMarketChargesFields
        charges={purchaseDraft.charges}
        onChange={(charges) => setPurchaseDraft({ ...purchaseDraft, charges })}
      />
      <TextField
        label="Comments"
        multiline
        minRows={2}
        size="small"
        fullWidth
        value={purchaseDraft.comments ?? ''}
        onChange={(e) => setPurchaseDraft({ ...purchaseDraft, comments: e.target.value })}
      />
      <PurchasePartnerAllocations
        partners={partners}
        rows={partnerRows}
        totalPurchaseCost={draftTotalCost}
        onChange={setPartnerRows}
      />
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" startIcon={<Save />} onClick={handleSavePurchase} disabled={saving}>
          {saving ? 'Saving…' : editingPurchaseId ? 'Update purchase' : 'Save purchase'}
        </Button>
        <Button size="small" onClick={() => setPurchaseEditor(null)} disabled={saving}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );

  const renderPurchaseCard = (p: PlanPurchase, index: number) => {
    const cost = purchaseTotalCost(p);
    const allocs = p.partnerAllocations ?? [];
    return (
      <Box key={p.id ?? `legacy-${index}`} sx={{ mb: 2 }}>
        {index > 0 && <Divider sx={{ mb: 2 }} />}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">
            Buy #{index + 1} · {p.tradeDate}
            {!p.id && (
              <Chip label="Legacy — save to persist" size="small" color="warning" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" onClick={() => openEditPurchaseForm(p)} disabled={saving || purchaseEditor !== null}>
              <Edit fontSize="small" />
            </IconButton>
            {p.id && (
              <IconButton
                size="small"
                color="error"
                onClick={() => void handleDeletePurchase(p.id!)}
                disabled={saving || purchaseEditor !== null}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Shares</TableCell>
              <TableCell>{p.quantity}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Price / share</TableCell>
              <TableCell>{formatCurrency(p.pricePerShare)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Gross</TableCell>
              <TableCell>{formatCurrency(grossAmount(p.quantity, p.pricePerShare))}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Charges</TableCell>
              <TableCell>{formatCurrency(sumCharges(p.charges))}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Lot cost</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(cost)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {allocs.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Partner split (this buy)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Partner</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocs.map((row) => (
                  <TableRow key={row.id ?? row.capitalPartnerId}>
                    <TableCell>
                      {row.partnerName || partners.find((x) => x.id === row.capitalPartnerId)?.name || '—'}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.allocatedAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  return (
    <Stack spacing={2.5}>
      <MarketValuationPanel
        valuation={marketValuation}
        loading={valuationLoading}
        stockName={plan.profile?.stockName ?? plan.planCode}
      />
      {report && <PlanExecutionReportSection report={report} />}
      <PlanProfitOverrideSection plan={plan} report={report} onSaved={onSaved} />

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ShoppingCart fontSize="small" color="primary" />
            <Typography variant="subtitle2" color="primary">
              Purchases
            </Typography>
            {purchases.length > 0 && (
              <Chip
                size="small"
                label={`${totalSharesBought(data)} shares · ${formatCurrency(data.totalPurchaseCost ?? 0)} total`}
                variant="outlined"
              />
            )}
          </Stack>
          {purchaseEditor === null && (
            <Button size="small" startIcon={<Add />} onClick={openNewPurchaseForm} disabled={saving}>
              Add purchase
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Record each buy under this plan. Split each purchase cost (gross + charges) across capital partners.
          Sells use FIFO across all buys.
        </Typography>

        {legacyUnsaved && purchaseEditor === null && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 1.5 }}>
            Legacy purchase data is not saved yet. Edit and save each buy, or save when recording a sell.
          </Alert>
        )}

        {purchaseEditor !== null ? (
          renderPurchaseForm()
        ) : purchases.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No purchases yet — add your first buy.
          </Typography>
        ) : (
          purchases.map((p, i) => renderPurchaseCard(p, i))
        )}

        {purchases.length > 0 && (data.partnerAllocations?.length ?? 0) > 0 && purchaseEditor === null && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Partner capital (all purchases combined)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Partner</TableCell>
                  <TableCell align="right">Total on plan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.partnerAllocations!.map((row) => (
                  <TableRow key={row.capitalPartnerId}>
                    <TableCell>
                      {row.partnerName || partners.find((p) => p.id === row.capitalPartnerId)?.name || '—'}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.allocatedAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Sell fontSize="small" color="warning" />
            <Typography variant="subtitle2" color="primary">
              Sells (partial allowed)
            </Typography>
            {purchases.length > 0 && (
              <Chip size="small" label={`${sharesRemaining} shares left`} variant="outlined" />
            )}
          </Stack>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => openSellDialog()}
            disabled={purchases.length === 0 || sharesRemaining <= 0 || saving}
          >
            Record sell
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Each sell can be a different date, quantity, and price. Gain type uses FIFO across purchase lots.
        </Typography>

        {data.sells.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No sells yet — position fully held.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Sell price</TableCell>
                <TableCell align="right">Proceeds</TableCell>
                <TableCell>Gain type</TableCell>
                <TableCell align="right">Charges</TableCell>
                <TableCell width={88} />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.sells.map((sell) => (
                <TableRow key={sell.id}>
                  <TableCell>{sell.tradeDate}</TableCell>
                  <TableCell align="right">{sell.quantity}</TableCell>
                  <TableCell align="right">{formatCurrency(sell.pricePerShare)}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(grossAmount(sell.quantity, sell.pricePerShare))}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={sell.capitalGainsType}
                      size="small"
                      color={sell.capitalGainsType === 'LTCG' ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(sumCharges(sell.charges))}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openSellDialog(sell)} disabled={saving}>
                      <Edit fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteSell(sell.id)}
                      disabled={saving}
                    >
                      <Delete fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <PlanExecutionTimeline events={timelineEvents} />

      <Dialog open={sellDialogOpen} onClose={() => setSellDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSellId ? 'Edit sell' : 'Record sell'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {purchases.length > 0 && (
              <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                Up to <strong>{maxSellQty}</strong> shares available across {purchases.length} purchase
                {purchases.length > 1 ? 's' : ''} ({totalSharesBought(data)} bought total)
              </Alert>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Sell date"
                type="date"
                size="small"
                fullWidth
                value={sellDraft.tradeDate}
                onChange={(e) => setSellDraft({ ...sellDraft, tradeDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Shares sold"
                type="number"
                size="small"
                fullWidth
                value={sellDraft.quantity || ''}
                onChange={(e) => setSellDraft({ ...sellDraft, quantity: Number(e.target.value) || 0 })}
                helperText={`Max ${maxSellQty}`}
              />
              <TextField
                label="Sell price / share (INR)"
                type="number"
                size="small"
                fullWidth
                value={sellDraft.pricePerShare || ''}
                onChange={(e) => setSellDraft({ ...sellDraft, pricePerShare: Number(e.target.value) || 0 })}
              />
            </Stack>
            {purchases.length > 0 && sellDraft.quantity > 0 && sellDraft.pricePerShare > 0 && (
              <Typography variant="caption" color="text.secondary">
                FIFO gain type:{' '}
                <strong>
                  {capitalGainsTypeForSell(purchases, {
                    ...sellDraft,
                    id: '',
                    capitalGainsType: 'STCG',
                  })}
                </strong>{' '}
                (STCG if any matched lot held &lt; 365 days)
              </Typography>
            )}
            <Button size="small" variant="text" onClick={applySuggestedSellCharges} sx={{ alignSelf: 'flex-start' }}>
              Suggest Indian delivery charges
            </Button>
            <IndianMarketChargesFields
              charges={sellDraft.charges}
              onChange={(charges) => setSellDraft({ ...sellDraft, charges })}
            />
            <TextField
              label="Comments"
              multiline
              minRows={2}
              size="small"
              fullWidth
              value={sellDraft.comments ?? ''}
              onChange={(e) => setSellDraft({ ...sellDraft, comments: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSellDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSell} disabled={saving}>
            {saving ? 'Saving…' : 'Save sell'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PlanExecutionPanel;
